import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIeKitLabels, useRestrictedTagLabel } from '@/hooks/useIeKitLabels';
import { useRestrictedProgramLabels } from '@/hooks/useRestrictedProgramLabels';
import type { ReactNode } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import RemoveIcon from '@mui/icons-material/Remove';
import { alpha } from '@mui/material/styles';
import { PricingCell } from './PricingCell';
import {
  useIEMBuyProgramCatalog,
  useIEMRestrictedProgramCatalog,
  usePHRItems,
  useIESummary,
} from '@/hooks/queries/iem/useIEMQueries';
import type { IEMBuyProgramCatalogItem } from '@/services/iem';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useNotifications, useNotificationStore } from '@/stores/useNotificationStore';
import { extractErrorMessage } from '../workflowUtils';
import type { IssueBuyProgramCartItem } from './types';
import { extractAuthorizedCatalogIds, filterAuthorizedBuyProgramItems } from './buyProgramUtils';
import {
  validatePricingInputs,
  parsePricingInput,
  isBuyProgramCartItemIncomplete,
} from './buyProgramPricing';
import { EXCLUDE_PNP_CATALOG_TYPE_KEEP_NULL, EXCLUDE_PNP_TYPE_KEEP_NULL } from '@/utils/iemFilters';

/** Pricing data from existing PHR items */
type ExistingPricing = {
  actualPrice: number | null;
  tax: number | null;
  shipping: number | null;
};

/** Default empty pricing state for form inputs */
const EMPTY_PRICING = { actualPrice: '', tax: '', shipping: '' } as const;

/** A restricted (buy) program catalog item eligible for bulk add, with its computed remaining/authorized/current quantities. */
type RestrictedProgramWithCeilingItem = {
  catalogItem: IEMBuyProgramCatalogItem;
  remainingAuthorizedQty: number;
  authorizedQty: number;
  currentQty: number;
};

export interface IssueBuyProgramTabResult {
  toolbar: ReactNode | null;
  content: ReactNode;
  cartItems: IssueBuyProgramCartItem[];
  actions: IssueBuyProgramTabActions;
  /** True (restricted program only) when at least one authorized, in-stock item has a configured ceiling price and can be bulk-added. */
  hasAddableItems: boolean;
  /** True (restricted program only) when the cart contains an item missing actualPrice/receiptDate that must be fixed before Complete. */
  hasIncompleteItems: boolean;
}

export interface IssueBuyProgramTabOptions {
  open: boolean;
  active?: boolean;
  customerId?: string;
  readOnly?: boolean;
  /** Override the catalog query hook. Defaults to useIEMBuyProgramCatalog.
   *  Pass useIEMRestrictedProgramCatalog to render the restricted (buy) program tab. */
  catalogHook?: typeof useIEMBuyProgramCatalog | typeof useIEMRestrictedProgramCatalog;
  /** Set to 'restricted' to render restricted (buy) program-specific labels and layout. */
  variant?: 'restricted';
}

export interface IssueBuyProgramTabActions {
  removeItem: (itemId: string) => void;
  adjustQuantity: (itemId: string, delta: number) => void;
  clear: () => void;
  /** Restricted-program-only bulk add; no-op for the CORE (non-restricted) variant. */
  addAllAvailable: () => void;
  /** Re-validates and commits actualPrice/tax/shipping for an existing cart item. */
  updatePricing: (
    itemId: string,
    pricing: { actualPrice: string; tax: string; shipping: string },
  ) => void;
  /** Commits a receipt date for an existing cart item. */
  updateReceiptDate: (itemId: string, date: string) => void;
}

export const useIssueBuyProgramTab = ({
  open,
  active = true,
  customerId,
  readOnly = false,
  catalogHook = useIEMBuyProgramCatalog,
  variant,
}: IssueBuyProgramTabOptions): IssueBuyProgramTabResult => {
  const isRestrictedProgram = variant === 'restricted';
  const isTabActive = open && active;
  const notifications = useNotifications();
  const { label: ieKitLabel, labelPlural: ieKitLabelPlural } = useIeKitLabels('front_window');
  const { programLabel } = useRestrictedProgramLabels();
  const restrictedTagLabel = useRestrictedTagLabel();
  const activeNotifications = useNotificationStore((state) => state.notifications);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 350);
  const [buyProgramItems, setBuyProgramItems] = useState<IssueBuyProgramCartItem[]>([]);
  const [issueQuantities, setIssueQuantities] = useState<Record<string, number>>({});
  const [selectedKit, setSelectedKit] = useState<string>('all');
  // State for inline pricing inputs (per item row)
  const [itemPricing, setItemPricing] = useState<
    Record<string, { actualPrice: string; tax: string; shipping: string }>
  >({});
  // Per-item receipt date (YYYY-MM-DD) — required before an item can be added (#458)
  const [itemReceiptDates, setItemReceiptDates] = useState<Record<string, string>>({});

  const {
    data = [],
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  } = catalogHook({
    searchTerm: debouncedSearch,
    enabled: isTabActive,
  });

  const {
    data: existingPhrItems = [],
    isLoading: phrLoading,
    isFetching: phrFetching,
  } = usePHRItems(
    customerId ?? '',
    {
      status: 'neq.closed',
      ...EXCLUDE_PNP_TYPE_KEEP_NULL,
    },
    isTabActive,
  );

  const { data: ieItems = [], isLoading: ieLoading } = useIESummary(
    customerId ?? '',
    EXCLUDE_PNP_CATALOG_TYPE_KEEP_NULL,
    isTabActive,
  );
  const buyProgramIeItems = useMemo(
    () =>
      ieItems.filter(
        (item) =>
          item.catalogType === 'buy program' ||
          (isRestrictedProgram && item.catalogType === 'soc buy program'),
      ),
    [ieItems, isRestrictedProgram],
  );

  // Build set of catalog IDs authorized via IE Kit
  const authorizedCatalogIds = useMemo(() => extractAuthorizedCatalogIds(ieItems), [ieItems]);

  // Filter catalog items to only those authorized via the customer's BOI/IE Kit.
  // For the restricted-program variant this enforces the operational rule that only
  // BOI-linked restricted (buy) program items are available — non-BOI restricted-program
  // items must not appear in Issue Out. For standard Buy Program the same authorization check applies.
  const authorizedBuyProgramItems = useMemo(
    () => filterAuthorizedBuyProgramItems(data, authorizedCatalogIds),
    [data, authorizedCatalogIds],
  );

  // Map catalogId -> Set of Kit Names
  const kitNamesByCatalogId = useMemo(() => {
    const map = new Map<string, Set<string>>();
    buyProgramIeItems.forEach((item) => {
      if (item.catalogId && item.kitName) {
        const existing = map.get(item.catalogId) ?? new Set();
        existing.add(item.kitName);
        map.set(item.catalogId, existing);
      }
    });
    return map;
  }, [buyProgramIeItems]);

  // Map catalogId -> first ieKitId (a buy program catalog is only in one kit per soldier)
  const ieKitIdByCatalogId = useMemo(() => {
    const map = new Map<string, string>();
    buyProgramIeItems.forEach((item) => {
      if (item.catalogId && item.ieKitId && !map.has(item.catalogId)) {
        map.set(item.catalogId, item.ieKitId);
      }
    });
    return map;
  }, [buyProgramIeItems]);

  // Calculate IE Kit stats (authorized vs current) by catalogId
  const ieStatsByCatalogId = useMemo(() => {
    const map = new Map<string, { authorizedQty: number; currentQty: number }>();
    buyProgramIeItems.forEach((item) => {
      if (item.catalogId) {
        const existing = map.get(item.catalogId) ?? {
          authorizedQty: 0,
          currentQty: item.currentQty,
        };
        // Accumulate authorizedQty if the item appears in multiple kits
        // Note: currentQty comes from the view which aggregates per person/catalog, so it should be same across rows
        // We use Math.max for currentQty just to be safe, though it should be identical.
        map.set(item.catalogId, {
          authorizedQty: existing.authorizedQty + item.authorizedQty,
          currentQty: Math.max(existing.currentQty, item.currentQty),
        });
      }
    });
    return map;
  }, [buyProgramIeItems]);

  // Extract unique available kits for filtering
  const availableKits = useMemo(() => {
    const kits = new Set<string>();
    buyProgramIeItems.forEach((item) => {
      if (item.kitName) {
        kits.add(item.kitName);
      }
    });
    return Array.from(kits).sort();
  }, [buyProgramIeItems]);

  useEffect(() => {
    if (!open) {
      setSearch('');
      setBuyProgramItems([]);
      setIssueQuantities({});
      setSelectedKit('all');
      setItemPricing({});
      setItemReceiptDates({});
    }
  }, [open]);

  const selectedIds = useMemo(
    () => new Set(buyProgramItems.map((item) => item.id)),
    [buyProgramItems],
  );

  // Map catalogId -> pricing from existing PHR items (for displaying pricing of assigned items)
  // Merges pricing fields from multiple PHR items if they have partial data
  const existingPricingByCatalogId = useMemo(() => {
    const map = new Map<string, ExistingPricing>();
    existingPhrItems.forEach((item) => {
      if (!item.catalogId) return;
      const existing = map.get(item.catalogId);
      if (!existing) {
        // First entry for this catalog - use its values
        map.set(item.catalogId, {
          actualPrice: item.actualPrice ?? null,
          tax: item.tax ?? null,
          shipping: item.shipping ?? null,
        });
      } else {
        // Merge: fill in any null fields from this item
        map.set(item.catalogId, {
          actualPrice: existing.actualPrice ?? item.actualPrice ?? null,
          tax: existing.tax ?? item.tax ?? null,
          shipping: existing.shipping ?? item.shipping ?? null,
        });
      }
    });
    return map;
  }, [existingPhrItems]);

  const handleAddItem = useCallback(
    (item: IEMBuyProgramCatalogItem) => {
      if (readOnly) return;

      // Get pricing from state and parse values
      const pricing = itemPricing[item.id] ?? EMPTY_PRICING;
      const actualPrice = parsePricingInput(pricing.actualPrice);
      const tax = parsePricingInput(pricing.tax);
      const shipping = parsePricingInput(pricing.shipping);

      // Validate pricing inputs using shared utility.
      // For restricted (buy) program items (isRestrictedProgram=true), a configured max price is required.
      const validation = validatePricingInputs(actualPrice, tax, shipping, item.unitPrice, {
        requireCeiling: isRestrictedProgram,
      });
      if (!validation.valid) {
        notifications.warning(validation.error);
        return;
      }

      // Receipt date is required on every line item (#458)
      const receiptDate = (itemReceiptDates[item.id] ?? '').trim();
      if (!receiptDate) {
        notifications.warning('Receipt date is required before adding this item.');
        return;
      }

      // Only block/warn when the requested quantity would push the member's total
      // (existing cart quantity + this add) past their remaining authorized quantity
      // (authorized - already issued). A brand-new first add that is within the
      // remaining authorized quantity must be allowed without any "already has"
      // warning (#1088).
      const stats = ieStatsByCatalogId.get(item.id);
      const authorizedQty = isRestrictedProgram
        ? Math.max(1, stats?.authorizedQty ?? 0)
        : (stats?.authorizedQty ?? 0);
      const alreadyIssuedQty = stats?.currentQty ?? 0;
      const remainingAuthorizedQty = Math.max(0, authorizedQty - alreadyIssuedQty);
      const requestedQuantity = Math.max(1, issueQuantities[item.id] ?? 1);
      const existingCartQuantity =
        buyProgramItems.find((entry) => entry.id === item.id)?.quantity ?? 0;
      const exceedsAuthorizedQty =
        existingCartQuantity + requestedQuantity > remainingAuthorizedQty;

      if (exceedsAuthorizedQty) {
        notifications.warning('Member already has this Buy Program item');
        return;
      }

      setBuyProgramItems((current) => {
        const quantity = Math.max(1, issueQuantities[item.id] ?? 1);
        const existingIndex = current.findIndex((entry) => entry.id === item.id);

        if (existingIndex >= 0) {
          const next = [...current];
          const existingItem = next[existingIndex];
          if (existingItem) {
            next[existingIndex] = {
              ...existingItem,
              quantity: existingItem.quantity + quantity,
              // Update pricing with latest values
              actualPrice,
              tax,
              shipping,
              receiptDate,
            };
          }
          return next;
        }

        const nextItem: IssueBuyProgramCartItem = {
          id: item.id,
          catalog: item,
          quantity,
          maxQuantity: Math.max(
            0,
            (isRestrictedProgram
              ? Math.max(1, ieStatsByCatalogId.get(item.id)?.authorizedQty ?? 0)
              : (ieStatsByCatalogId.get(item.id)?.authorizedQty ?? 0)) -
              (ieStatsByCatalogId.get(item.id)?.currentQty ?? 0),
          ),
          actualPrice,
          tax,
          shipping,
          receiptDate,
          ieKitId: ieKitIdByCatalogId.get(item.id) ?? null,
        };
        return [...current, nextItem];
      });
      // Reset quantity to 1, but retain pricing inputs for potential additional adds
      setIssueQuantities((prev) => ({ ...prev, [item.id]: 1 }));
      // NOTE: Pricing inputs are intentionally retained so users can easily add more
      // of the same item with the same pricing
    },
    [
      notifications,
      readOnly,
      issueQuantities,
      buyProgramItems,
      isRestrictedProgram,
      ieStatsByCatalogId,
      ieKitIdByCatalogId,
      itemPricing,
      itemReceiptDates,
    ],
  );

  const handleRemoveItem = useCallback(
    (itemId: string) => {
      if (readOnly) return;
      setBuyProgramItems((current) => current.filter((item) => item.id !== itemId));
    },
    [readOnly],
  );

  const handleAdjustQuantity = useCallback(
    (itemId: string, delta: number) => {
      if (readOnly) return;

      const item = buyProgramItems.find((i) => i.id === itemId);
      if (!item) return;

      if (delta > 0) {
        const stats = ieStatsByCatalogId.get(itemId);
        // For restricted-program items, fall back to authorizedQty=1 so kit-stats gaps don't block issuance.
        const authorizedQty = isRestrictedProgram
          ? Math.max(1, stats?.authorizedQty ?? 0)
          : (stats?.authorizedQty ?? 999999);
        const currentQty = stats?.currentQty ?? 0;
        const remainingAuthorized = Math.max(0, authorizedQty - currentQty);
        const proposedQty = item.quantity + delta;

        // If increasing, enforce IE Kit limit
        if (proposedQty > remainingAuthorized) {
          const warningTitle = 'Cannot issue more than authorized quantity.';
          // Prevent duplicate warnings
          const alreadyShowing = activeNotifications.some(
            (n) => n.type === 'warning' && n.title === warningTitle,
          );

          if (!alreadyShowing) {
            notifications.warning(warningTitle);
          }
          return;
        }
      }

      setBuyProgramItems((current) =>
        current.map((entry) => {
          if (entry.id === itemId) {
            return { ...entry, quantity: Math.max(1, entry.quantity + delta) };
          }
          return entry;
        }),
      );
    },
    [readOnly, ieStatsByCatalogId, notifications, buyProgramItems, activeNotifications],
  );

  // Restricted-program-only: split authorized+available items into those with a configured ceiling
  // price (bulk-addable) and those missing one (must be skipped and reported).
  // Each entry is filtered to remainingAuthorizedQty > 0 (nothing left to issue is excluded).
  const restrictedProgramAddableItems = useMemo(() => {
    if (!isRestrictedProgram) {
      return {
        withCeiling: [] as RestrictedProgramWithCeilingItem[],
        missingCeiling: [] as IEMBuyProgramCatalogItem[],
      };
    }

    const withCeiling: RestrictedProgramWithCeilingItem[] = [];
    const missingCeiling: IEMBuyProgramCatalogItem[] = [];

    authorizedBuyProgramItems.forEach((item) => {
      const stats = ieStatsByCatalogId.get(item.id);
      const authorizedQty = Math.max(1, stats?.authorizedQty ?? 0);
      const currentQty = stats?.currentQty ?? 0;
      const existingCartQuantity =
        buyProgramItems.find((entry) => entry.id === item.id)?.quantity ?? 0;
      const remainingAuthorizedQty = Math.max(0, authorizedQty - currentQty - existingCartQuantity);

      if (remainingAuthorizedQty <= 0) return;

      if (item.unitPrice != null) {
        withCeiling.push({ catalogItem: item, remainingAuthorizedQty, authorizedQty, currentQty });
      } else {
        missingCeiling.push(item);
      }
    });

    return { withCeiling, missingCeiling };
  }, [isRestrictedProgram, authorizedBuyProgramItems, ieStatsByCatalogId, buyProgramItems]);

  const handleAddAllAvailable = useCallback(() => {
    if (!isRestrictedProgram || readOnly) return;

    const { withCeiling, missingCeiling } = restrictedProgramAddableItems;

    if (withCeiling.length > 0) {
      setBuyProgramItems((current) => {
        const next = [...current];
        withCeiling.forEach(
          ({ catalogItem, remainingAuthorizedQty, authorizedQty, currentQty }) => {
            const maxQuantity = Math.max(0, authorizedQty - currentQty);
            const existingIndex = next.findIndex((entry) => entry.id === catalogItem.id);

            if (existingIndex >= 0) {
              const existingItem = next[existingIndex];
              if (existingItem) {
                next[existingIndex] = {
                  ...existingItem,
                  quantity: existingItem.quantity + remainingAuthorizedQty,
                  maxQuantity,
                };
              }
              return;
            }

            next.push({
              id: catalogItem.id,
              catalog: catalogItem,
              quantity: remainingAuthorizedQty,
              maxQuantity,
              actualPrice: 0,
              tax: 0,
              shipping: 0,
              receiptDate: undefined,
              ieKitId: ieKitIdByCatalogId.get(catalogItem.id) ?? null,
            });
          },
        );
        return next;
      });
    }

    if (missingCeiling.length > 0) {
      const names = missingCeiling.map((item) => item.name).join(', ');
      notifications.warning(
        `Skipped ${missingCeiling.length} item${missingCeiling.length === 1 ? '' : 's'} without a configured max price`,
        names,
      );
    }
  }, [
    isRestrictedProgram,
    readOnly,
    restrictedProgramAddableItems,
    ieKitIdByCatalogId,
    notifications,
  ]);

  const handleUpdatePricing = useCallback(
    (itemId: string, pricing: { actualPrice: string; tax: string; shipping: string }) => {
      if (readOnly) return;

      const cartItem = buyProgramItems.find((entry) => entry.id === itemId);
      if (!cartItem) return;

      const actualPrice = parsePricingInput(pricing.actualPrice);
      const tax = parsePricingInput(pricing.tax);
      const shipping = parsePricingInput(pricing.shipping);

      const validation = validatePricingInputs(
        actualPrice,
        tax,
        shipping,
        cartItem.catalog.unitPrice,
        {
          requireCeiling: isRestrictedProgram,
        },
      );
      if (!validation.valid) {
        notifications.warning(validation.error);
        return;
      }

      setBuyProgramItems((current) =>
        current.map((entry) =>
          entry.id === itemId ? { ...entry, actualPrice, tax, shipping } : entry,
        ),
      );
    },
    [readOnly, buyProgramItems, isRestrictedProgram, notifications],
  );

  const handleUpdateReceiptDate = useCallback(
    (itemId: string, date: string) => {
      if (readOnly) return;

      const receiptDate = date.trim();
      if (!receiptDate) {
        notifications.warning('Receipt date is required before adding this item.');
        return;
      }

      setBuyProgramItems((current) =>
        current.map((entry) => (entry.id === itemId ? { ...entry, receiptDate } : entry)),
      );
    },
    [readOnly, notifications],
  );

  const hasAddableItems =
    isRestrictedProgram && restrictedProgramAddableItems.withCeiling.length > 0;
  const hasIncompleteItems =
    isRestrictedProgram && buyProgramItems.some(isBuyProgramCartItemIncomplete);

  const handleRetry = useCallback(() => {
    refetch();
  }, [refetch]);

  const toolbar = useMemo(() => {
    return (
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
      >
        <Box>
          <Typography variant="subtitle2">
            {isRestrictedProgram
              ? `Authorized ${programLabel} Items`
              : 'Authorized Buy Program Items'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isRestrictedProgram
              ? `${programLabel} items authorized for this member via their ${restrictedTagLabel} ${ieKitLabel}.`
              : `Buy Program items authorized for this member via their ${ieKitLabel}.`}
          </Typography>
        </Box>
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            select
            size="small"
            label="Filter by Kit"
            value={selectedKit}
            onChange={(e) => setSelectedKit(e.target.value)}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="all">
              {isRestrictedProgram ? `All ${restrictedTagLabel}` : `All ${ieKitLabelPlural}`}
            </MenuItem>
            {availableKits.map((kit) => (
              <MenuItem key={kit} value={kit}>
                {kit}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            label="Search buy program catalog"
            placeholder="Search by LIN, NSN, or name"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Escape' && search) {
                event.preventDefault();
                event.stopPropagation();
                setSearch('');
              }
            }}
            InputProps={{
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch('')} aria-label="Clear search">
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
            sx={{ width: { xs: '100%', sm: 320 } }}
          />
        </Stack>
      </Stack>
    );
  }, [
    search,
    selectedKit,
    availableKits,
    isRestrictedProgram,
    ieKitLabel,
    ieKitLabelPlural,
    programLabel,
    restrictedTagLabel,
  ]);

  const content = useMemo(() => {
    if (!open) {
      return <Box sx={{ minHeight: 240 }} />;
    }

    const loadingSection = (
      <Paper
        variant="outlined"
        sx={{ minHeight: 240, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <CircularProgress size={24} />
      </Paper>
    );

    if (isLoading || ieLoading) {
      return <Stack spacing={2}>{loadingSection}</Stack>;
    }

    // Filter rows based on selected kit
    const rows = authorizedBuyProgramItems
      .filter((item) => {
        if (selectedKit === 'all') return true;
        const itemKits = kitNamesByCatalogId.get(item.id);
        return itemKits?.has(selectedKit) ?? false;
      })
      .sort((a, b) => {
        const left = (a.name ?? '').trim();
        const right = (b.name ?? '').trim();
        const nameCompare = left.localeCompare(right, undefined, { sensitivity: 'base' });
        if (nameCompare !== 0) {
          return nameCompare;
        }
        return (a.lin ?? '').localeCompare(b.lin ?? '', undefined, { sensitivity: 'base' });
      });

    const tableSection = (
      <Paper variant="outlined" sx={{ overflow: 'hidden', maxHeight: '70vh' }}>
        {isFetching && !isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={20} />
          </Box>
        ) : null}
        {isError ? (
          <Box sx={{ p: 3 }}>
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={handleRetry}>
                  Retry
                </Button>
              }
            >
              {extractErrorMessage(error, 'Unable to load buy program catalog items.')}
            </Alert>
          </Box>
        ) : (
          <Box sx={{ overflow: 'auto', maxHeight: '70vh' }}>
            <Table
              size="small"
              aria-label="Buy program catalog"
              sx={{ '& .MuiTableCell-root': { px: 1 } }}
            >
              <TableHead>
                <TableRow>
                  {!isRestrictedProgram && <TableCell>{ieKitLabel}</TableCell>}
                  <TableCell>LIN</TableCell>
                  <TableCell>NSN</TableCell>
                  <TableCell>Nomenclature</TableCell>
                  <TableCell align="right">
                    {isRestrictedProgram ? `${restrictedTagLabel} QTY` : `${ieKitLabel} Qty`}
                  </TableCell>
                  <TableCell align="right">{isRestrictedProgram ? 'HR QTY' : 'HR Qty'}</TableCell>
                  <TableCell align="right">Max Price</TableCell>
                  <TableCell align="right" sx={{ width: 100, minWidth: 100, maxWidth: 100 }}>
                    Actual Price
                  </TableCell>
                  <TableCell align="center" sx={{ width: 100, minWidth: 100, maxWidth: 100 }}>
                    Tax
                  </TableCell>
                  <TableCell align="right" sx={{ width: 100, minWidth: 100, maxWidth: 100 }}>
                    Shipping
                  </TableCell>
                  <TableCell align="center" sx={{ minWidth: 128 }}>
                    Receipt Date
                  </TableCell>
                  <TableCell align="right">Issue Qty</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={isRestrictedProgram ? 13 : 14}
                      align="center"
                      sx={{ py: 4 }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {authorizedBuyProgramItems.length === 0
                          ? 'No authorized Buy Program items for this member.'
                          : 'No items found for the selected kit filter.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((item) => {
                    const lin = item.lin ?? '—';
                    const nsn = item.nsn ?? '—';
                    const isSelected = selectedIds.has(item.id);

                    const stats = ieStatsByCatalogId.get(item.id);
                    // Restricted-program items are authorized via soc_tag; IE kit stats may be absent
                    // or show authorizedQty=0 when the kit view hasn't synced the item.
                    // Fall back to authorizedQty=1, currentQty=0 so the item is always addable.
                    const authorizedQty = isRestrictedProgram
                      ? Math.max(1, stats?.authorizedQty ?? 0)
                      : (stats?.authorizedQty ?? 0);
                    const currentQty = stats?.currentQty ?? 0;

                    // Find quantity already in cart
                    const cartItem = buyProgramItems.find((ci) => ci.id === item.id);
                    const qtyInCart = cartItem?.quantity ?? 0;

                    const highlightRow = currentQty > 0 || qtyInCart > 0;
                    const issueQty = issueQuantities[item.id] ?? 1;

                    // Remaining allowed is authorized - (on_hand + in_cart)
                    const remainingAuthorized = Math.max(0, authorizedQty - currentQty - qtyInCart);

                    // Determine status chip
                    let statusLabel = 'Not Issued';
                    let statusColor: 'default' | 'success' | 'warning' | 'info' = 'default';

                    if (currentQty >= authorizedQty) {
                      statusLabel = 'Fully Assigned';
                      statusColor = 'success';
                    } else if (currentQty > 0) {
                      statusLabel = `Assigned ${currentQty}`;
                      statusColor = 'info';
                    } else {
                      statusLabel = 'Not Assigned';
                      statusColor = 'default';
                    }

                    const kits =
                      Array.from(kitNamesByCatalogId.get(item.id) ?? []).join(', ') || '—';

                    const isLimitExceeded = issueQty > remainingAuthorized;
                    const canAdd = !isLimitExceeded && remainingAuthorized > 0;

                    // For fully assigned items, show existing pricing as read-only text
                    const existingPricing = existingPricingByCatalogId.get(item.id);
                    const showExistingPricing =
                      currentQty > 0 && remainingAuthorized <= 0 && qtyInCart <= 0;

                    const actionButton = (
                      <IconButton
                        size="small"
                        color="primary"
                        aria-label="Add to cart"
                        onClick={() => handleAddItem(item)}
                        disabled={phrLoading || phrFetching || readOnly || !canAdd}
                      >
                        <AddIcon fontSize="inherit" />
                      </IconButton>
                    );

                    const wrappedAddAction =
                      phrLoading || phrFetching ? (
                        <Tooltip title="Checking existing hand receipts…">
                          <span>{actionButton}</span>
                        </Tooltip>
                      ) : remainingAuthorized <= 0 ? (
                        <Tooltip title="Member has reached the authorized quantity.">
                          <span>{actionButton}</span>
                        </Tooltip>
                      ) : isLimitExceeded ? (
                        <Tooltip title={`Cannot issue more than ${remainingAuthorized} items.`}>
                          <span>{actionButton}</span>
                        </Tooltip>
                      ) : (
                        actionButton
                      );

                    return (
                      <TableRow
                        key={item.id}
                        hover
                        sx={
                          highlightRow
                            ? {
                                backgroundColor: (theme) =>
                                  alpha(theme.palette.warning.light, 0.12),
                                '&:hover': {
                                  backgroundColor: (theme) =>
                                    alpha(theme.palette.warning.light, 0.18),
                                },
                              }
                            : undefined
                        }
                      >
                        {!isRestrictedProgram && (
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            <Typography variant="body2">{kits}</Typography>
                          </TableCell>
                        )}
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{lin}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{nsn}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {item.name}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">{authorizedQty}</TableCell>
                        <TableCell align="right">{currentQty}</TableCell>
                        <TableCell align="right">
                          {item.unitPrice != null ? `$${item.unitPrice.toFixed(2)}` : '—'}
                        </TableCell>
                        <PricingCell
                          value={existingPricing?.actualPrice}
                          isReadOnly={showExistingPricing}
                          inputValue={itemPricing[item.id]?.actualPrice ?? ''}
                          onChange={(value) => {
                            setItemPricing((prev) => ({
                              ...prev,
                              [item.id]: {
                                ...(prev[item.id] ?? EMPTY_PRICING),
                                actualPrice: value,
                              },
                            }));
                          }}
                          disabled={readOnly || (remainingAuthorized <= 0 && qtyInCart <= 0)}
                          width="100px"
                          showError={
                            itemPricing[item.id]?.actualPrice !== '' &&
                            item.unitPrice != null &&
                            parseFloat(itemPricing[item.id]?.actualPrice || '0') > item.unitPrice
                          }
                        />
                        <PricingCell
                          value={existingPricing?.tax}
                          isReadOnly={false}
                          inputValue={itemPricing[item.id]?.tax ?? ''}
                          onChange={(value) => {
                            setItemPricing((prev) => ({
                              ...prev,
                              [item.id]: {
                                ...(prev[item.id] ?? EMPTY_PRICING),
                                tax: value,
                              },
                            }));
                          }}
                          disabled={readOnly}
                          width="100px"
                        />
                        <PricingCell
                          value={existingPricing?.shipping}
                          isReadOnly={showExistingPricing}
                          inputValue={itemPricing[item.id]?.shipping ?? ''}
                          onChange={(value) => {
                            setItemPricing((prev) => ({
                              ...prev,
                              [item.id]: {
                                ...(prev[item.id] ?? EMPTY_PRICING),
                                shipping: value,
                              },
                            }));
                          }}
                          disabled={readOnly || (remainingAuthorized <= 0 && qtyInCart <= 0)}
                          width="100px"
                        />
                        <TableCell align="center">
                          <TextField
                            type="date"
                            size="small"
                            required
                            value={itemReceiptDates[item.id] ?? ''}
                            onChange={(e) => {
                              const val = e.target.value;
                              setItemReceiptDates((prev) => ({ ...prev, [item.id]: val }));
                            }}
                            disabled={readOnly || (remainingAuthorized <= 0 && qtyInCart <= 0)}
                            error={!(itemReceiptDates[item.id] ?? '').trim()}
                            slotProps={{ inputLabel: { shrink: true } }}
                            sx={{
                              width: '128px',
                              '& .MuiOutlinedInput-root': {
                                bgcolor: 'background.paper',
                              },
                              '& .MuiOutlinedInput-input': {
                                py: 0.75,
                              },
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <TextField
                            type="number"
                            size="small"
                            value={issueQty}
                            onChange={(e) => {
                              const val = Math.max(1, parseInt(e.target.value) || 0);
                              // Enforce limit in input
                              const cappedVal = Math.min(val, Math.max(1, remainingAuthorized));
                              setIssueQuantities((prev) => ({ ...prev, [item.id]: cappedVal }));
                            }}
                            disabled={readOnly || remainingAuthorized <= 0}
                            slotProps={{
                              htmlInput: {
                                min: 1,
                                max: Math.max(1, remainingAuthorized),
                                style: { textAlign: 'right' },
                              },
                            }}
                            sx={{
                              width: '70px',
                              '& .MuiOutlinedInput-root': {
                                bgcolor: 'background.paper',
                                '&:not(.Mui-disabled)': {
                                  bgcolor: 'background.paper',
                                },
                              },
                              '& .MuiOutlinedInput-input': {
                                py: 0.75,
                              },
                            }}
                            error={isLimitExceeded}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={statusLabel}
                            color={statusColor}
                            variant="filled"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            {isSelected ? (
                              <Tooltip title="Remove from cart">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  aria-label="Remove from cart"
                                  onClick={() => handleRemoveItem(item.id)}
                                  disabled={readOnly}
                                >
                                  <RemoveIcon fontSize="inherit" />
                                </IconButton>
                              </Tooltip>
                            ) : null}
                            {wrappedAddAction}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </Box>
        )}
      </Paper>
    );

    return <Stack spacing={2}>{tableSection}</Stack>;
  }, [
    open,
    isLoading,
    ieLoading,
    isFetching,
    authorizedBuyProgramItems,
    isError,
    error,
    handleRetry,
    handleAddItem,
    handleRemoveItem,
    phrLoading,
    phrFetching,
    readOnly,
    selectedIds,
    issueQuantities,
    selectedKit,
    kitNamesByCatalogId,
    ieStatsByCatalogId,
    buyProgramItems,
    itemPricing,
    existingPricingByCatalogId,
    itemReceiptDates,
    isRestrictedProgram,
    ieKitLabel,
    restrictedTagLabel,
  ]);

  return {
    toolbar,
    content,
    cartItems: buyProgramItems,
    hasAddableItems,
    hasIncompleteItems,
    actions: useMemo(
      () => ({
        removeItem: handleRemoveItem,
        adjustQuantity: handleAdjustQuantity,
        clear: () => setBuyProgramItems([]),
        addAllAvailable: handleAddAllAvailable,
        updatePricing: handleUpdatePricing,
        updateReceiptDate: handleUpdateReceiptDate,
      }),
      [
        handleRemoveItem,
        handleAdjustQuantity,
        handleAddAllAvailable,
        handleUpdatePricing,
        handleUpdateReceiptDate,
      ],
    ),
  };
};
